import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card } from 'react-bootstrap';
import { galleryAPI } from '../services/api';

const GalleryForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    upload_date: new Date().toISOString().split('T')[0]
  });
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string>('');

  useEffect(() => {
    if (isEdit) {
      loadGalleryItem();
    }
  }, [id]);

  const loadGalleryItem = async () => {
    try {
      const response = await galleryAPI.getById(String(id));
      const item = response.data;
      setFormData({
        title: item.title || '',
        description: item.description || '',
        upload_date: item.upload_date ? item.upload_date.split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } catch (err) {
      setError('Failed to load gallery item');
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      
      // Create preview URL
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEdit) {
        await galleryAPI.update(String(id), formData);
      } else {
        if (!file) {
          setError('Please select a file');
          setLoading(false);
          return;
        }
        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('upload_date', formData.upload_date);
        data.append('file', file);
        await galleryAPI.create(data);
      }

      navigate('/gallery');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save gallery item');
    } finally {
      setLoading(false);
    }
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '3rem' }}>
      <Container style={{ maxWidth: '900px' }}>
        <div className="mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            {isEdit ? 'Edit Gallery Item' : 'Upload New Item'}
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px', margin: 0 }}>
            {isEdit ? 'Update gallery item information' : 'Upload a new photo or video to the gallery'}
          </p>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '24px'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '32px' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Enter a title for this item"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Add a description (optional)"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Upload Date</Form.Label>
                <Form.Control
                  type="date"
                  name="upload_date"
                  value={formData.upload_date}
                  onChange={handleChange}
                  style={inputStyle}
                />
              </Form.Group>

              {!isEdit && (
                <>
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>
                      File <span style={{ color: '#ff4d4f' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*,video/*"
                      onChange={handleFileChange}
                      required
                      style={inputStyle}
                    />
                    <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                      Upload photos (JPG, PNG, GIF) or videos (MP4, MOV, AVI)
                    </Form.Text>
                  </Form.Group>

                  {previewUrl && file && (
                    <div className="mb-4">
                      <Form.Label style={labelStyle}>Preview</Form.Label>
                      <Card style={{ 
                        border: '1px solid #f0f0f0',
                        borderRadius: '6px',
                        overflow: 'hidden'
                      }}>
                        {file.type.startsWith('image/') ? (
                          <img 
                            src={previewUrl} 
                            alt="Preview" 
                            style={{ 
                              width: '100%', 
                              maxHeight: '400px',
                              objectFit: 'contain',
                              backgroundColor: '#fafafa'
                            }}
                          />
                        ) : file.type.startsWith('video/') ? (
                          <video 
                            src={previewUrl} 
                            controls
                            style={{ 
                              width: '100%', 
                              maxHeight: '400px',
                              backgroundColor: '#000'
                            }}
                          />
                        ) : null}
                        <Card.Body style={{ 
                          padding: '12px 16px',
                          backgroundColor: '#fafafa'
                        }}>
                          <small style={{ color: '#8c8c8c', fontSize: '13px' }}>
                            <strong style={{ color: '#595959' }}>Filename:</strong> {file.name}<br />
                            <strong style={{ color: '#595959' }}>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB<br />
                            <strong style={{ color: '#595959' }}>Type:</strong> {file.type}
                          </small>
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </>
              )}

              {isEdit && (
                <div className="mb-4" style={{
                  padding: '16px',
                  backgroundColor: '#e6f4ff',
                  border: '1px solid #91caff',
                  borderRadius: '6px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '18px', marginRight: '12px' }}>ℹ️</span>
                    <div>
                      <strong style={{ color: '#0958d9', fontSize: '14px' }}>Note:</strong>
                      <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#004085' }}>
                        You can only edit the title, description, and upload date. To change the file, please delete this item and upload a new one.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="d-flex gap-2 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                <Button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#004a85';
                  }}
                  onMouseOut={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#00579a';
                  }}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Item' : 'Upload Item')}
                </Button>
                <Button 
                  onClick={() => navigate('/gallery')}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#00579a';
                    e.currentTarget.style.color = '#00579a';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
  transition: 'all 0.2s ease'
};

export default GalleryForm;